// Story 6.6 — session-scoped fidelity degrade flag.
//
// When the runtime FPS guard (useCeremonyFpsGuard) sees the reveal
// ceremony dropping frames on a real device, it latches this flag for
// the rest of the SPA session. `useEffectiveMobileFidelity()` ORs it into
// Story 3.7's viewport signal, so every subsequent ceremony renders at
// the reduced mobile-fidelity tier — fewer burst particles, no heavy
// shadow blurs — matching the epic AC:
//
//   "auto-degrade detection in production: … if dropped frames detected
//    during ceremony, falls back to mobile-fidelity tier (Story 3.7) for
//    that session."
//
// State pattern: a module-level external store read through
// `useSyncExternalStore` — the SAME primitive the SPA already uses for
// matchMedia (lib/useMatchMedia.ts). The project pulls zustand in as a
// dependency but has no store yet; a hand-rolled external store keeps
// this feature dependency-light and matches the established pattern.
//
// Deliberately NOT persisted: the flag is per-session by design (a
// reload re-measures on the fresh page). It resets on a full reload
// because the module is re-evaluated.

import { useSyncExternalStore } from "react";

let degraded = false;
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((l) => l());
}

/** Latch the session into the reduced mobile-fidelity tier. Idempotent —
 *  only notifies subscribers on the first transition. Called by the FPS
 *  guard when dropped frames are detected. */
export function degradeFidelityForSession(): void {
  if (degraded) return;
  degraded = true;
  emit();
}

/** Non-reactive read (for the FPS guard, which must not re-run on every
 *  render). */
export function isSessionFidelityDegraded(): boolean {
  return degraded;
}

/** Test-only reset. Not used in app code — the flag is session-scoped and
 *  only clears on a real reload. */
export function resetSessionFidelity(): void {
  if (!degraded && listeners.size === 0) return;
  degraded = false;
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Reactive subscription — components re-render when the session degrades. */
export function useSessionFidelityDegraded(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => degraded,
    () => false, // SSR/no-window snapshot
  );
}
